// TODO: Catch?
export const asyncEvery = async (
  promises: Promise<boolean>[],
): Promise<boolean> => {
  return (await Promise.all(promises)).every((result) => result);
};

// TODO: catch?
export const asyncSome = async (
  promises: Promise<boolean>[],
): Promise<boolean> => {
  return (await Promise.all(promises)).some((result) => result);
};

// async counterpart to Array.prototype.find
export async function asyncFind<T>(
  arr: T[],
  cb: (el: T) => Promise<boolean>,
): Promise<T | undefined> {
  for (const el of arr) {
    if (await cb(el)) {
      return el;
    }
  }
}

export async function collectAsyncStream<T>(
  stream: AsyncIterable<T>,
): Promise<T[]> {
  const items: T[] = [];
  for await (const item of stream) {
    items.push(item);
  }
  return items;
}

/**
 * @param delay how long to sleep for in ms
 * @param signal rejects with the signal's reason, and stops waiting, once it aborts
 *
 * Basically an async/await wrapper around `setTimeout`
 */
export function sleep(
  delay: number,
  { signal }: { signal?: AbortSignal } = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, delay);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Converts a synchronous `for` loop into an async `for await` loop that batches its iterations
 * to prevent the loop from hogging the javascript event loop for CPU intensive tasks.
 *
 * @param elements Iterable to iterate over
 * @param maxBatchTime The maximum time in ms the loop should execute before sleeping
 * @param delay How long in ms to sleep between each batch
 */
export async function* yieldingIterable<T>(
  elements: Iterable<T>,
  maxBatchTime: number,
  delay = 0,
): AsyncIterable<T> {
  let lastPause = performance.now();
  for (const element of elements) {
    if (performance.now() - lastPause > maxBatchTime) {
      // eslint-disable-next-line no-await-in-loop -- being able to optionally sleep during iteration is the point of this utility
      await sleep(delay);
      lastPause = performance.now();
    }
    yield element;
  }
}

/**
 * One pending callback. Starting it again replaces the pending run, and
 * cancelling drops it, so an owner that cancels on teardown never sees a stale
 * run. The timeout doesn't keep a Node process alive.
 */
export class Timer {
  private handle?: ReturnType<typeof setTimeout>;

  get pending(): boolean {
    return this.handle != null;
  }

  /** Runs `run` after `ms`, in place of any run already pending. */
  start(ms: number, run: () => void): void {
    this.cancel();
    this.handle = setTimeout(() => {
      this.handle = undefined;
      run();
    }, ms);
    // Only Node's timers have `unref`.
    (this.handle as { unref?: () => void }).unref?.();
  }

  cancel(): void {
    clearTimeout(this.handle);
    this.handle = undefined;
  }
}

/**
 * Calls @param callback periodically (every @param periodMs milliseconds)
 * until @param promise is settled.
 */
export async function periodicallyWhile<T>(
  promise: Promise<T>,
  callback: () => void,
  periodMs: number,
): Promise<T> {
  const timer = new Timer();
  const check = (): void => {
    callback();
    timer.start(periodMs, check);
  };
  timer.start(periodMs, check);

  try {
    return await promise;
  } finally {
    timer.cancel();
  }
}

export interface Repeater {
  /** The run in progress, if any. Never rejects, so it's safe to await. */
  readonly inFlight: Promise<void> | undefined;
  /** No further runs start. Resolves once a run already in progress settles. */
  stop: () => Promise<void>;
}

/**
 * Runs `run` every `getIntervalMs()` milliseconds until stopped, waiting for
 * each run to settle before scheduling the next, so runs never overlap. The
 * interval is read before each wait, so it can change while repeating. The
 * timers don't keep a Node process alive.
 *
 * `run` should handle its own errors: a rejection is ignored, so one failed
 * run doesn't stop the repeat.
 */
export function repeat({
  getIntervalMs,
  run,
}: {
  run: () => Promise<void>;
  getIntervalMs: () => number;
}): Repeater {
  let stopped = false;
  const timer = new Timer();
  let inFlight: Promise<void> | undefined;
  const schedule = (): void => {
    timer.start(getIntervalMs(), () => {
      const running = run().then(
        () => undefined,
        () => undefined,
      );
      inFlight = running;
      void running.then(() => {
        inFlight = undefined;
        if (!stopped) {
          schedule();
        }
      });
    });
  };
  schedule();
  return {
    get inFlight() {
      return inFlight;
    },
    stop: async () => {
      stopped = true;
      timer.cancel();
      await inFlight;
    },
  };
}
